import type {
  AreaFacets,
  QnA,
  SearchFacet,
  SearchWidget,
} from '@bridebook/toolbox/src/seo-facets/get-seo-data';
import type { IUISupplier, Slug } from '@bridebook/toolbox/src/types';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { ILocationData } from '@bridebook/web/app-shared/lib/search/types';
import { formatFiltersFromSearch } from '@bridebook/web/lib/search/utils';
import type { SearchResponseFields } from '@bridebook/web/lib/search/types';
import type { IFetchSearchPromiseResult } from '@bridebook/web/lib/types';

export type BasicSearchState = {
  list: IUISupplier[];
  filters: Record<string, any>;
  totalResults: number;
  actualCategory: Slug;
  fields: SearchResponseFields | null;
  facetsContent: SearchFacet | null;
  faqContent: QnA[];
  relatedAreas: string[];
  areaFacets: AreaFacets | null;
  loaded: boolean;
  searchWidgets: SearchWidget[];
  source: string | null;
  selectedMarkerId: string;
  highlightedMarkerId: string;
  locationData: Record<string, ILocationData>;
};

const initialState: BasicSearchState = {
  list: [],
  totalResults: 0,
  actualCategory: 'venue',
  fields: null,
  facetsContent: null,
  faqContent: [],
  relatedAreas: [],
  areaFacets: {},
  loaded: false,
  filters: {},
  searchWidgets: [],
  source: null,
  selectedMarkerId: '',
  highlightedMarkerId: '',
  locationData: {},
};

const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    startSearch: (state) => {
      state.loaded = false;
    },
    saveSearchResult: (state, { payload }: PayloadAction<IFetchSearchPromiseResult>) => {
      state.list = payload.results;
      state.totalResults = payload.totalResults;
      state.actualCategory = payload.fields?.type || '';
      state.fields = { ...payload.fields, page: payload.fields.page || 1 };
      state.facetsContent = payload.facetsContent || null;
      state.faqContent = payload.faqContent || [];
      state.relatedAreas = payload.relatedAreas || [];
      state.searchWidgets = payload.searchWidgets || [];
      state.areaFacets = payload.areaFacets || null;
      state.loaded = true;
      state.filters = formatFiltersFromSearch(payload.filters);
    },
    setActiveMarkerId: (state, { payload }: PayloadAction<{ id: string }>) => {
      state.selectedMarkerId = payload.id;
    },
    setHighlightedMarkerId: (state, { payload }: PayloadAction<{ id: string }>) => {
      state.highlightedMarkerId = payload.id;
    },
    saveLocationData: (
      state,
      { payload }: PayloadAction<{ area: string; locationData: ILocationData }>,
    ) => {
      state.locationData[payload.area] = payload.locationData;
    },
  },
});

export const {
  saveSearchResult,
  startSearch,
  setActiveMarkerId,
  setHighlightedMarkerId,
  saveLocationData,
} = searchSlice.actions;

export default searchSlice.reducer;
